import { SidebarPage } from '@backstage/core-components';
import {
  coreExtensionData,
  createExtension,
  createExtensionInput,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import { SearchModalProvider } from '@backstage/plugin-search';
import { PortalSearchModal } from './PortalSearchModal';
import { PortalShell } from './PortalShell';

/*
 * Replaces the stock app layout to add a persistent 52px breadcrumb bar above the
 * routed content. Like `modules/theme`, this is a same-id override: an extension
 * named `layout` in an `app`-pluginId module takes the id `app/layout` and stands
 * in for `@backstage/plugin-app`'s `AppLayout`.
 *
 * The bar renders inside `SidebarPage` (right of the fixed sidebar, which the
 * sidebar's own left padding reserves), stacked above `content` in a full-height
 * column. `hone-portal.css` already shrinks `[data-backstage-core-page]` by the
 * header height so the page fits beside the bar without a second scrollbar.
 *
 * `PortalShell` picks the desktop chrome (this breadcrumb bar) or the mobile
 * chrome (top app bar + bottom tab bar) at the phone breakpoint. Below it the
 * `padding-left` gutter and header-height rules in `hone-portal.css` are gated
 * to `min-width: 600px`, and Backstage's own MobileSidebar is hidden.
 *
 * Inputs/output mirror the stock `AppLayout` exactly so the `nav` and `content`
 * singletons keep attaching from `AppNav` / `AppRoutes`.
 */
const honeLayout = createExtension({
  name: 'layout',
  attachTo: { id: 'app/root', input: 'children' },
  inputs: {
    nav: createExtensionInput([coreExtensionData.reactElement], {
      singleton: true,
    }),
    content: createExtensionInput([coreExtensionData.reactElement], {
      singleton: true,
    }),
  },
  output: [coreExtensionData.reactElement],
  factory: ({ inputs }) => [
    coreExtensionData.reactElement(
      /*
       * The search modal's state lives above both the nav and the content so
       * the sidebar trigger, the ⌘K binding and any future header affordance
       * drive one dialog. `PortalSearchModal` renders that single instance.
       */
      <SearchModalProvider>
        <SidebarPage>
          {inputs.nav.get(coreExtensionData.reactElement)}
          <PortalShell>
            {inputs.content.get(coreExtensionData.reactElement)}
          </PortalShell>
          <PortalSearchModal />
        </SidebarPage>
      </SearchModalProvider>,
    ),
  ],
});

export const layoutModule = createFrontendModule({
  pluginId: 'app',
  extensions: [honeLayout],
});
